import { createFileRoute, redirect } from "@tanstack/react-router";
import { getUserWithProfile, type CombinedUser } from "@/lib/collections/workerProfile-Users/getUserWithProfile";
import { useRealtimeCollection } from "@/hooks/realtime/useRealtimeCollection";
import { getUserSessionsAndBreaks } from "@/lib/collections/datapicker/getSessionsAndBreaks";
import WorkLog from "@/user/workLog";
import { useCallback, useMemo, useState } from "react";

export const Route = createFileRoute('/_authenticated/workLog')({
  beforeLoad: ({ context }) => {
    if (!context.user) {
      throw redirect({ to: '/login' });
    }
  },

  loader: async ({ context }) => {
    const basicUser = context.user!;
    const combinedUser = await getUserWithProfile(basicUser.$id);
    if (!combinedUser) {
      throw new Response("Not Found", { status: 404 });
    }
    return { user: combinedUser };
  },

  component: RouteComponent,
});

function RouteComponent() {
  const { user } = Route.useLoaderData() as { user: CombinedUser };

  const dbId = useMemo(() => import.meta.env.VITE_APPWRITE_DATABASE_ID!, []);
  const collectionIds = useMemo(() => [
    import.meta.env.VITE_WORK_SESSIONS_COLLECTION_ID!,
    import.meta.env.VITE_WORK_BREAKS_COLLECTION_ID!,
    import.meta.env.VITE_WORK_SESSION_NOTES_COLLECTION_ID!
  ], []);

  // Memoize the fetcher function to prevent re-creating
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const fetcher = useCallback(() => {
    if (!user.profile?.userId) {
      throw new Error('User profile not found');
    }
    const dateStr = selectedDate.toISOString().split('T')[0];
    return getUserSessionsAndBreaks(user.profile.userId, dateStr);
  }, [user.profile?.userId, selectedDate]);
  const filterEvents = useMemo(() => ['create', 'update', 'delete'], []);

  const [sessionsWithBreaks, _isLoading] = useRealtimeCollection(
    dbId,
    collectionIds,
    fetcher,
    filterEvents
  );

  return <WorkLog user={user} sessionsWithBreaks={sessionsWithBreaks}
    onDateChange={setSelectedDate}
    selectedDate={selectedDate} />;
}
